import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertCircle, CheckCircle, AlertTriangle, XCircle, Clock, ArrowLeftRight } from "lucide-react";
import { useToolLifecycle } from "@/utils/trackingHooks";
import LeadForm from "@/forms/engine/LeadForm";
import toolSwitchVsStay, { CONTRACT_STATUS_INPUT_LABEL } from "@/forms/definitions/toolSwitchVsStay";
import { CONTRACT_STATUS_OPTIONS } from "@/forms/definitions/common";

const SVS_ITEM_NAME = "switch_vs_stay_calculator";
const SVS_ITEM_OPTS = { itemType: "calculator" };

const CONTRACT_OPTIONS = [
  { value: "out_of_contract", label: "Out of contract" },
  { value: "less_than_6", label: "Less than 6 months left" },
  { value: "six_to_twelve", label: "6\u201312 months left" },
  { value: "over_twelve", label: "More than 12 months left" },
  { value: "not_sure", label: "Not sure" },
];

const CONTRACT_MESSAGES = {
  out_of_contract: "You appear to have flexibility to switch now if the numbers make sense.",
  less_than_6: "If you are close to contract end, it may be worth planning your switch now.",
  six_to_twelve: "It may be worth comparing the break-even point with your remaining contract term.",
  over_twelve: "A longer remaining contract term may affect whether switching now is worthwhile.",
  not_sure: "Checking your current contract term could help refine the recommendation.",
};

function formatGBP(val) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(val);
}

const SWITCH_VS_STAY_CATEGORY_LABEL = {
  switch_now: "Switch now",
  worth_reviewing: "Worth reviewing",
  review_later: "Review later",
  no_immediate_saving: "No immediate saving",
};

// Example figures the calculator opens with (results show straight away).
const DEFAULT_INPUTS = {
  currentMonthlyCost: "300", newMonthlyCost: "220",
  exitFee: "0", setupCost: "0", hardwareCost: "0",
  contractStatus: "out_of_contract",
};

const INPUT_LABELS = {
  currentMonthlyCost: "Current monthly cost",
  newMonthlyCost: "Estimated new monthly cost",
  exitFee: "Exit fee",
  setupCost: "Setup cost",
  hardwareCost: "Hardware cost",
  contractStatus: CONTRACT_STATUS_INPUT_LABEL,
};

const numberOrUndefined = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};
const round2 = (n) => (Number.isFinite(n) ? Math.round(n * 100) / 100 : undefined);
const compact = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

/** Calculator inputs + result as readable `context` for the Lead (see toolSwitchVsStay.jsx). */
function buildLeadContext(inputs, result) {
  const leftAtDefault = Object.keys(INPUT_LABELS)
    .filter((key) => inputs[key] === DEFAULT_INPUTS[key])
    .map((key) => INPUT_LABELS[key]);
  return compact({
    tool: "Switch vs Stay Calculator",
    category: SWITCH_VS_STAY_CATEGORY_LABEL[result.category] || result.category,
    current_monthly_cost_gbp: numberOrUndefined(inputs.currentMonthlyCost),
    new_monthly_cost_gbp: numberOrUndefined(inputs.newMonthlyCost),
    exit_fee_gbp: numberOrUndefined(inputs.exitFee),
    setup_cost_gbp: numberOrUndefined(inputs.setupCost),
    hardware_cost_gbp: numberOrUndefined(inputs.hardwareCost),
    total_switching_cost_gbp: round2(result.totalSwitchingCost),
    monthly_saving_gbp: round2(result.monthlySaving),
    annual_saving_gbp: round2(result.monthlySaving * 12),
    // null when there is no monthly saving — omitted rather than sent as 0.
    break_even_months: result.breakEvenMonths !== null ? Math.round(result.breakEvenMonths * 10) / 10 : undefined,
    first_year_net_saving_gbp: round2(result.firstYearNetSaving),
    contract_status: CONTRACT_STATUS_OPTIONS.find((o) => o.value === inputs.contractStatus)?.label,
    left_at_default: leftAtDefault.length ? leftAtDefault : undefined,
  });
}

function calculateSwitchVsStay(inputs) {
  const current = parseFloat(inputs.currentMonthlyCost) || 0;
  const newCost = parseFloat(inputs.newMonthlyCost) || 0;
  const exit = parseFloat(inputs.exitFee) || 0;
  const setup = parseFloat(inputs.setupCost) || 0;
  const hardware = parseFloat(inputs.hardwareCost) || 0;

  const monthlySaving = current - newCost;
  const totalSwitchingCost = exit + setup + hardware;
  const breakEvenMonths = monthlySaving > 0 ? totalSwitchingCost / monthlySaving : null;
  const firstYearNetSaving = (monthlySaving * 12) - totalSwitchingCost;

  let category, headline, description;

  if (monthlySaving <= 0) {
    category = "no_immediate_saving";
    headline = "Switching may not reduce your costs right now";
    description = "Based on the figures entered, switching does not currently appear to produce a clear monthly saving. It may still be worth reviewing pricing structure, support, or contract timing.";
  } else if (breakEvenMonths !== null && breakEvenMonths <= 6) {
    category = "switch_now";
    headline = "Switching could pay off quickly";
    description = "Based on the figures entered, the savings could recover your switching costs relatively quickly.";
  } else if (breakEvenMonths !== null && breakEvenMonths <= 12) {
    category = "worth_reviewing";
    headline = "Switching may still be worth it";
    description = "Based on the figures entered, the savings could still outweigh the switching costs within a year.";
  } else {
    category = "review_later";
    headline = "It may be better to review this later";
    description = "Based on the figures entered, the break-even point is relatively long. It may still be worth planning a future switch or reviewing your figures in more detail.";
  }

  return {
    monthlySaving, totalSwitchingCost, breakEvenMonths, firstYearNetSaving,
    category, headline, description,
    contractMessage: CONTRACT_MESSAGES[inputs.contractStatus] || "",
  };
}

function CurrencyInput({ label, name, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">£</span>
        <input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step="0.01" min="0" className="w-full pl-8 pr-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all" data-testid={testId} />
      </div>
    </div>
  );
}

function ResultCard({ label, value, variant = "default", testId }) {
  const bg = { default: "bg-white border border-brand-light-grey", primary: "bg-brand-aqua-light border border-brand-aqua/20", annual: "bg-gradient-to-br from-brand-aqua-light to-white border border-brand-aqua/30", negative: "bg-brand-pink-light border border-brand-pink/20" };
  const text = { default: "text-brand-black", primary: "text-brand-aqua-dark", annual: "text-brand-aqua-dark", negative: "text-brand-pink" };
  return (
    <div className={`${bg[variant]} rounded-xl p-4`} data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-wider text-brand-grey mb-1">{label}</p>
      <p className={`text-xl sm:text-2xl font-heading font-bold ${text[variant]}`}>{value}</p>
    </div>
  );
}

function ResultsDisplay({ result }) {
  const catConfig = {
    switch_now: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
    worth_reviewing: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-50 border-amber-200" },
    review_later: { icon: Clock, color: "text-amber-500", bg: "bg-amber-50 border-amber-200" },
    no_immediate_saving: { icon: XCircle, color: "text-red-500", bg: "bg-red-50 border-red-200" },
  };
  const cfg = catConfig[result.category];
  const Icon = cfg.icon;
  const savingPositive = result.monthlySaving > 0;
  const fyPositive = result.firstYearNetSaving > 0;

  return (
    <div className="space-y-4 pt-2" data-testid="switch-result">
      <div className="grid grid-cols-3 gap-3">
        <ResultCard label="Estimated monthly saving" value={formatGBP(result.monthlySaving)} variant={savingPositive ? "primary" : "negative"} testId="switch-monthly-saving" />
        <ResultCard label="Break-even point" value={result.breakEvenMonths !== null ? `${result.breakEvenMonths.toFixed(1)} months` : "N/A"} testId="switch-breakeven" />
        <ResultCard label="First-year net saving" value={formatGBP(result.firstYearNetSaving)} variant={fyPositive ? "annual" : "negative"} testId="switch-fy-saving" />
      </div>

      <div className={`rounded-xl border p-5 ${cfg.bg}`}>
        <div className="flex items-start gap-3">
          <Icon size={22} className={`${cfg.color} flex-shrink-0 mt-0.5`} />
          <div>
            <h4 className={`font-heading font-bold text-brand-black mb-1`} data-testid="switch-headline">{result.headline}</h4>
            <p className="text-sm text-brand-grey leading-relaxed">{result.description}</p>
          </div>
        </div>
      </div>

      {result.contractMessage && (
        <p className="text-xs text-brand-grey italic bg-gray-50 rounded-xl px-4 py-3" data-testid="switch-contract-msg">{result.contractMessage}</p>
      )}

      <div className="grid grid-cols-3 gap-3">
        <ResultCard label="Current monthly cost" value={formatGBP(parseFloat(result.currentMonthlyCost) || 0)} testId="switch-current" />
        <ResultCard label="Estimated new cost" value={formatGBP(parseFloat(result.newMonthlyCost) || 0)} testId="switch-new" />
        <ResultCard label="Total switching cost" value={formatGBP(result.totalSwitchingCost)} testId="switch-total-cost" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Link to="/switch-and-save" className="flex-1 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine" data-testid="switch-cta-review">Get my tailored savings review <ArrowRight size={16} /></Link>
        <a href="/book" className="flex-1 bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors" data-testid="switch-cta-speak">Speak to the team</a>
      </div>
    </div>
  );
}

function useSwitchCalculator() {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const { markStarted, markCompleted } = useToolLifecycle(SVS_ITEM_NAME, SVS_ITEM_OPTS);

  const handleChange = useCallback((e) => {
    markStarted();
    const { name, value } = e.target;
    setInputs((p) => ({ ...p, [name]: value }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMinInput = (parseFloat(inputs.currentMonthlyCost) || 0) > 0;

  const result = useMemo(() => {
    if (!hasMinInput) return null;
    return { ...calculateSwitchVsStay(inputs), currentMonthlyCost: inputs.currentMonthlyCost, newMonthlyCost: inputs.newMonthlyCost };
  }, [inputs, hasMinInput]);  

  useEffect(() => { if (result) markCompleted(); }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  return { inputs, handleChange, result, hasMinInput };
}

function SwitchInputs({ inputs, handleChange }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CurrencyInput label="Current monthly payment cost" name="currentMonthlyCost" value={inputs.currentMonthlyCost} onChange={handleChange} placeholder="300" testId="switch-current-cost" />
        <CurrencyInput label="Estimated new monthly payment cost" name="newMonthlyCost" value={inputs.newMonthlyCost} onChange={handleChange} placeholder="220" testId="switch-new-cost" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <CurrencyInput label="Exit fee / termination cost" name="exitFee" value={inputs.exitFee} onChange={handleChange} placeholder="0" testId="switch-exit-fee" />
        <CurrencyInput label="Setup / onboarding cost" name="setupCost" value={inputs.setupCost} onChange={handleChange} placeholder="0" testId="switch-setup-cost" />
        <CurrencyInput label="Hardware replacement cost" name="hardwareCost" value={inputs.hardwareCost} onChange={handleChange} placeholder="0" testId="switch-hardware-cost" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-brand-black mb-1.5">What is your current contract position?</label>
        <select name="contractStatus" value={inputs.contractStatus} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all appearance-none" data-testid="switch-contract">
          {CONTRACT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </>
  );
}

export default function SwitchVsStayCalculator({ className = "" }) {
  const { inputs, handleChange, result, hasMinInput } = useSwitchCalculator();
  const leadContext = useMemo(() => (result ? buildLeadContext(inputs, result) : null), [inputs, result]);

  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="switch-vs-stay-calculator">
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
        <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center"><ArrowLeftRight size={20} className="text-white" /></div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">When would switching pay off?</h3>
          <p className="text-xs text-brand-grey">Estimate how long it could take for switching to start delivering real savings</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-5">
        <SwitchInputs inputs={inputs} handleChange={handleChange} />
        {!hasMinInput && (
          <div className="flex items-start gap-2 text-xs text-brand-grey bg-gray-50 rounded-xl p-3">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> Enter your current monthly cost to see the comparison.
          </div>
        )}
        {result && <ResultsDisplay result={result} />}
        {result && (
          <div className="border-t border-brand-light-grey/60 pt-6">
            <h4 className="font-heading text-lg font-bold text-brand-black text-center mb-1">Get your personalised review</h4>
            <p className="text-xs text-brand-grey text-center mb-4">Tell us where to send your tailored follow-up.</p>
            <LeadForm schema={toolSwitchVsStay} context={leadContext} variant="compact" />
          </div>
        )}

        <p className="text-[11px] text-brand-grey leading-relaxed text-center">
          This calculator provides an illustration based on the figures entered. It is not a formal quote or financial recommendation. Actual savings and switching costs may vary depending on contract terms, pricing structure, hardware, setup requirements, and transaction profile.
        </p>
      </div>
    </div>
  );
}
